import customFetch from '../../utils/customFetch'
import { API_ENDPOINT, BUILD_NUMBER } from '../../config'
import { multimediaToApp } from './converters/multimediaToApp'
import { multimediaLinkUploadResponseToApp } from './converters/multimediaLinkUploadResponseToApp'
import { Attachment, VideoInfoProcess, VideoTicket } from '../../@types/types'
import { videoUploadTicketResponseToApp } from './converters/videoUploadTicketResponseToApp'
import { getDefaultLogger } from '../../services/logger/logger'

export const deleteMultimediaRequest = async (id: string, source?: string, size?: number) => {
  const options = {
    method: 'DELETE',
    headers: {
      Accept: 'application/json; charset=utf-8',
    },
    credentials: 'include',
  }
  const url = new URL(`${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/multimedia/remove/${id}`)
  source && url.searchParams.append('s', source)
  size && url.searchParams.append('u', size.toString())

  await customFetch(url.toString(), options)
}

export const updateNameRequest = async (id: string, name: string, source: string, size?: number) => {
  const options = {
    method: 'POST',
    headers: {
      Accept: 'text/plain; charset=utf-8',
    },
    credentials: 'include',
    body: name,
  }
  const url = new URL(`${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/multimedia/change/${id}`)
  url.searchParams.append('s', source)
  size && url.searchParams.append('u', size.toString())

  const response = await customFetch(url.toString(), options)
  return response.text()
}

export const updatePictureRequest = async (id: string, image: Blob, source: string, size?: number) => {
  const data = new FormData()
  data.append('file', image)

  const options = {
    method: 'PUT',
    headers: {
      Accept: 'application/json; charset=utf-8',
    },
    credentials: 'include',
    body: data,
  }

  const url = new URL(`${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/multimedia/replace/${id}`)
  url.searchParams.append('source', source)
  size && url.searchParams.append('size', size.toString())

  // @ts-ignore
  const response = await customFetch(url.toString(), options)
  return multimediaToApp(await response.json())
}

export const uploadMultimediaAttachment = async (file: File, isFloorPlan = false): Promise<Attachment> => {
  let responseStatus: number | undefined

  try {
    const data = new FormData()
    data.append('file', file)
    data.append('isFloorPlan', String(isFloorPlan))

    const options = {
      method: 'POST',
      headers: {
        Accept: 'application/json; charset=utf-8',
      },
      credentials: 'include' as const,
      body: data,
    }

    const response = await customFetch(
      `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/multimedia/upload`,
      // @ts-ignore node-fetch RequestInit typing does not align with browser FormData in this codebase
      options
    )
    responseStatus = response.status

    if (!response.ok) {
      throw new Error(`Cannot upload multimedia attachment, failed with ${response.status}`)
    }

    return multimediaToApp(await response.json())
  } catch (error) {
    getDefaultLogger().warn('MULTIMEDIA_UPLOAD_FAILED', {
      buildNumber: BUILD_NUMBER,
      errorMessage: error instanceof Error ? error.message : String(error),
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      httpStatus: responseStatus,
      isFloorPlan,
      uploadClient: 'fetch',
    })
    throw error
  }
}

export const uploadMultimediaLink = async (title: string, linkUrl: string): Promise<Attachment> => {
  const options = {
    method: 'POST',
    headers: {
      Accept: 'application/json; charset=utf-8',
      'Content-Type': 'application/json; charset=UTF-8',
    },
    credentials: 'include',
    body: JSON.stringify({ title, linkUrl }),
  }
  const response = await customFetch(
    `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/multimedia/links`,
    options
  )

  if (response.status != 201) {
    throw new Error(
      `Cannot upload multimedia link, failed with ${response.status} and message ${await response.text()}`
    )
  }
  return multimediaLinkUploadResponseToApp(await response.json())
}

export const videoUploadTicket = async (): Promise<VideoTicket> => {
  const options = {
    method: 'GET',
    headers: {
      Accept: 'application/json; charset=utf-8',
      'Content-Type': 'application/json; charset=UTF-8',
    },
    credentials: 'include',
  }
  const response = await customFetch(
    `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/video-upload-ticket`,
    options
  )

  if (response.status !== 200) {
    throw new Error(
      `Cannot upload multimedia upload video ticket, failed with ${
        response.status
      } and message ${await response.text()}`
    )
  }
  return videoUploadTicketResponseToApp(await response.json())
}

export const createMultimediaVideoAttachment = async (
  videoId: string,
  offerApiRealEstateId: number
): Promise<Attachment> => {
  const options = {
    method: 'POST',
    headers: {
      Accept: 'application/json; charset=utf-8',
      'Content-Type': 'application/json; charset=UTF-8',
    },
    credentials: 'include',
    body: JSON.stringify({ videoId }),
  }
  const response = await customFetch(
    `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/realEstate/offer-api/${offerApiRealEstateId}/video`,
    options
  )
  if (response.status !== 200) {
    throw new Error(
      `Cannot upload multimedia video, failed with ${response.status} and message ${await response.text()}`
    )
  }
  return (await response.json()) as Attachment
}

export const getVideoProcessingStatus = async (
  offerApiRealEstateId: number,
  offerApiId: string
): Promise<VideoInfoProcess> => {
  const options = {
    method: 'GET',
    headers: {
      Accept: 'application/json; charset=utf-8',
      'Content-Type': 'application/json; charset=UTF-8',
    },
    credentials: 'include',
  }
  const response = await customFetch(
    `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/realEstate/offer-api/${offerApiRealEstateId}/video/${offerApiId}`,
    options
  )
  if (response.status !== 200) {
    throw new Error(
      `Cannot get multimedia processing video status, failed with ${
        response.status
      } and message ${await response.text()}`
    )
  }
  return (await response.json()) as VideoInfoProcess
}
